import React, { useEffect, useState } from "react";
import { loadStyles } from "../../utils/cssLoader";
import { getData } from "../../utils/getData";
import { getImgUrl } from "../../utils/constants";

/**
 * F1ChampionshipStandings — "Classifica Campionato" (F1 championship standings).
 *
 * Two tabs: Drivers (formula1/driver-standings) and Constructors (formula1/team-standings).
 * Self-contained: serverData seeds the drivers tab for SSR; the constructors tab and the
 * embed path self-fetch. No Home/Away, no race selector — just the season standings.
 *
 * Driver row:      { pid, nm, teamId, team, pts, pos, tnm }
 * Constructor row: { teamId, team, pts, pos, tnm }
 */

const rowsFromRaw = (raw) => {
  const d = raw?.data ?? raw;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const mapDriver = (r) => ({
  id: r?.pid ?? r?.id,
  name: r?.nm || "",
  team: r?.team || "",
  teamId: r?.teamId,
  pos: r?.pos ?? null,
  pts: r?.pts ?? 0,
});
const mapTeam = (r) => ({
  id: r?.teamId ?? r?.id,
  name: r?.team || "",
  pos: r?.pos ?? null,
  pts: r?.pts ?? 0,
});
const byPos = (a, b) => (a.pos ?? 1e9) - (b.pos ?? 1e9);

// Images built from ids against the CDN root (no /api/v1). Missing → hidden via onError.
const driverImg = (id) =>
  id ? `${getImgUrl()}/v1/sports/motorsport/driver-pictures/${id}.png` : null;
const teamImg = (id) => (id ? `${getImgUrl()}/v1/sports/team-logos/${id}.png` : null);

const podiumClass = (pos) =>
  pos === 1 ? " f1__row--p1" : pos === 2 ? " f1__row--p2" : pos === 3 ? " f1__row--p3" : "";

const Avatar = ({ src, name, kind }) => (
  <span className={`f1__crest f1__crest--${kind}`}>
    {src ? (
      <img
        src={src}
        alt={name ? `${name}` : ""}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : null}
  </span>
);

const F1ChampionshipStandings = ({
  serverData = null,
  data: dataProp = null,
  dataId,
  baseUrl,
  sport = "formula1",
  loadCSS = true,
  dataClient = false,
  title = "Championship Standings",
}) => {
  useEffect(() => {
    loadStyles("f1Standings");
  }, []);

  const seed = rowsFromRaw(serverData || dataProp);
  const [tab, setTab] = useState("drivers");
  const [expanded, setExpanded] = useState(false);
  const [drivers, setDrivers] = useState(() => seed.map(mapDriver).sort(byPos));
  const [teams, setTeams] = useState([]);
  const [season, setSeason] = useState(() => seed[0]?.tnm || "");

  const selectTab = (t) => {
    setTab(t);
    setExpanded(false); // collapse back to the top 5 when switching tabs
  };

  // Hydration: re-read the serialized driver payload off the container.
  useEffect(() => {
    if (!dataClient) return;
    try {
      const el = document.querySelector(".microservice-cont");
      const parsed = JSON.parse(el?.getAttribute("data-listingdata") || "null");
      const rows = rowsFromRaw(parsed?.data);
      if (rows.length) {
        setDrivers(rows.map(mapDriver).sort(byPos));
        setSeason(rows[0]?.tnm || "");
      }
    } catch (_) {}
  }, [dataClient]);

  // Embed: self-fetch drivers if we have none.
  useEffect(() => {
    if (drivers.length || !baseUrl) return;
    let cancelled = false;
    getData({ dataType: "f1-driver-standings", baseUrl, sport }).then((res) => {
      if (cancelled) return;
      const rows = rowsFromRaw(res);
      if (rows.length) {
        setDrivers(rows.map(mapDriver).sort(byPos));
        setSeason(rows[0]?.tnm || "");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [drivers.length, baseUrl, sport]);

  // Constructors are never in the SSR payload — fetch them on the client.
  useEffect(() => {
    if (teams.length || !baseUrl) return;
    let cancelled = false;
    getData({ dataType: "f1-team-standings", baseUrl, sport }).then((res) => {
      if (cancelled) return;
      const rows = rowsFromRaw(res);
      if (rows.length) setTeams(rows.map(mapTeam).sort(byPos));
    });
    return () => {
      cancelled = true;
    };
  }, [teams.length, baseUrl, sport]);

  if (!drivers.length && !teams.length) {
    return (
      <div className="f1 f1--loading">
        <span>Loading standings…</span>
      </div>
    );
  }

  const isDrivers = tab === "drivers";
  const rows = isDrivers ? drivers : teams;
  const loadingTab = !isDrivers && !teams.length;
  const shown = expanded ? rows : rows.slice(0, 5); // top 5 until "Show more"
  const canExpand = rows.length > 5;

  return (
    <div className="f1">
      {/* Header */}
      <div className="f1__head">
        <h3 className="f1__title">{title}</h3>
      </div>

      {/* Tabs */}
      <div className="f1__tabs" role="tablist">
        <button
          type="button"
          className={`f1__tab${isDrivers ? " f1__tab--on" : ""}`}
          onClick={() => selectTab("drivers")}
        >
          Drivers
        </button>
        <button
          type="button"
          className={`f1__tab${!isDrivers ? " f1__tab--on" : ""}`}
          onClick={() => selectTab("constructors")}
        >
          Constructors
        </button>
      </div>

      {/* Table — capped scroll only once expanded */}
      <div className={`f1__scroll${expanded ? " f1__scroll--cap" : ""}`}>
        <table className="f1__table">
          <thead>
            <tr>
              <th className="f1__c-pos">POS</th>
              <th className="f1__c-name">{isDrivers ? "DRIVER" : "TEAM"}</th>
              <th className="f1__c-pts">POINTS</th>
            </tr>
          </thead>
          <tbody>
            {loadingTab ? (
              <tr>
                <td className="f1__empty" colSpan={3}>
                  Loading…
                </td>
              </tr>
            ) : (
              shown.map((r, i) => (
                <tr key={r.id ?? i} className={`f1__row${podiumClass(r.pos)}`}>
                  <td className="f1__c-pos">
                    <span className="f1__pos">{r.pos ?? i + 1}</span>
                  </td>
                  <td className="f1__c-name">
                    <span className="f1__ent">
                      <Avatar
                        src={isDrivers ? driverImg(r.id) : teamImg(r.id)}
                        name={r.name}
                        kind={isDrivers ? "driver" : "team"}
                      />
                      <span className="f1__name">{r.name}</span>
                    </span>
                  </td>
                  <td className="f1__c-pts">{r.pts}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Show more / less */}
      {canExpand && !loadingTab ? (
        <button
          type="button"
          className="f1__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
          <span className="f1__more-ar" aria-hidden="true">{expanded ? "‹" : "›"}</span>
        </button>
      ) : null}
    </div>
  );
};

export default F1ChampionshipStandings;
